import { useRef, useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import { Link2 } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { DatePickerInput } from '@/shared/ui/date-picker-input'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Checkbox } from '@/shared/ui/checkbox'
import { Textarea } from '@/shared/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'
import {
  LotFormAttachmentsSection,
  type PendingFile,
} from '@/features/lot-form-attachments/LotFormAttachmentsSection'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { formatBetweenBounds, parseNumericRangeBounds } from '@/shared/lib/data-field-bounds'
import { canAddNumberAcceptableRule } from '@/shared/lib/data-field-config'
import { instrumentLotPath, instrumentLotsPath } from '@/shared/lib/routes'
import type { WebInstrumentsLotForm } from '@/shared/types/web'
import { LotNotesBanner } from '@/features/instrument-lots/LotNotesBanner'

export interface LotFormProps {
  instrumentId: number
  formData: WebInstrumentsLotForm
  onSuccess?: (newLotDetailId?: number) => void
  onCancel?: () => void
  /** When true, appends ?from_modal=1 to the submit URL so the controller redirects back */
  fromModal?: boolean
  /**
   * Inertia partial-reload scope for modal submits. When set, the submit only refreshes
   * these props instead of doing a full-page reload, keeping the parent modal mounted.
   */
  reloadOnly?: string[]
}

const FieldError = ({ message }: { message?: string }) => {
  if (!message) return null
  return <p className="text-sm text-destructive mt-1">{message}</p>
}

const INSTRUMENT_LOT_MAIN_FORM_DOM_IDENTIFIER = 'instrument-lot-main-form'

/** Local calendar date as YYYY-MM-DD, matching the DatePickerInput value format. */
const todayDateStringForLotValidation = (): string => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

type LotFormData = {
  lot_name: string
  lot_notes: string
  lot_is_kit: boolean
  lot_requires_validation: boolean
  lot_number: string
  manufacturer: string
  product: string
  description: string
  date_received: string
  date_opened: string
  date_in_use: string
  expiration_date: string
  opened_expiration: string
  date_discarded: string
  other_identifier: string
  comments: string
  use_all_secondary: boolean
  is_confirmed: boolean
}

type DraftSecondaryLineForCreate = {
  clientId: string
  secondaryLotId?: number
  secondaryLotName: string
  secondaryLotDescription: string
  lotNumber: string
  dateReceived: string
  expirationDate: string
  dateDiscarded: string
}

type DataFieldValueDraft = {
  tempId: string
  existingId: number | null
  validationTypeId: string
  value: string
  value2: string
  isAcceptable: boolean
  operatorType: 'OR' | 'AND' | ''
}

type DefinedValueAcceptabilityDraft = Record<number, boolean> // definedValueId → isAcceptable

export const LotForm = ({
  instrumentId,
  formData,
  onSuccess,
  onCancel,
  fromModal = false,
  reloadOnly,
}: LotFormProps) => {
  const {
    lot,
    lotDetail,
    cloneSourceLotDetail,
    secondaryLots,
    secondaryLotDetails,
    isEdit,
    isAddingDetail,
    lotDetailId,
    lotIdForDetail,
    documentAttachments,
    lotTaskDataFields,
  } = formData

  const catalogSecondaryLotsForLotRow = secondaryLots ?? []

  const optionalLotFormDateFieldPlaceholder = t('instruments.lotsForm.placeholders.optionalDate')

  const permittedToCreateLotAttachmentDocuments = useCan(Resources.ATTACHMENT, Actions.CREATE)
  const permittedToDeleteLotAttachmentDocuments = useCan(Resources.ATTACHMENT, Actions.DELETE)
  const canSubmitLot = useCan(Resources.LOT_MANAGEMENT, isEdit ? Actions.UPDATE : Actions.CREATE)

  const seededLotDetailForFormFields =
    lotDetail ?? (!isEdit && isAddingDetail ? (cloneSourceLotDetail ?? null) : null)

  const isCloningLotDetailFromExistingCatalogLotRow =
    !isEdit && isAddingDetail && cloneSourceLotDetail != null

  const form = useForm<LotFormData>({
    lot_name: lot?.name ?? '',
    lot_notes: lot?.notes ?? '',
    lot_is_kit: catalogSecondaryLotsForLotRow.length > 0 ? true : Boolean(lot?.isKit ?? false),
    lot_requires_validation: lot?.requiresValidation ?? false,
    lot_number: seededLotDetailForFormFields?.lotNumber ?? '',
    manufacturer: seededLotDetailForFormFields?.manufacturer ?? '',
    product: seededLotDetailForFormFields?.product ?? '',
    description: seededLotDetailForFormFields?.description ?? '',
    date_received: seededLotDetailForFormFields?.dateReceived?.split('T')[0] ?? '',
    date_opened: seededLotDetailForFormFields?.dateOpened?.split('T')[0] ?? '',
    date_in_use: seededLotDetailForFormFields?.dateInUse?.split('T')[0] ?? '',
    expiration_date: isCloningLotDetailFromExistingCatalogLotRow
      ? ''
      : (seededLotDetailForFormFields?.expirationDate?.split('T')[0] ?? ''),
    opened_expiration: isCloningLotDetailFromExistingCatalogLotRow
      ? ''
      : (seededLotDetailForFormFields?.openedExpiration?.split('T')[0] ?? ''),
    date_discarded: isCloningLotDetailFromExistingCatalogLotRow
      ? ''
      : (seededLotDetailForFormFields?.dateDiscarded?.split('T')[0] ?? ''),
    other_identifier: seededLotDetailForFormFields?.otherIdentifier ?? '',
    comments: seededLotDetailForFormFields?.comments ?? '',
    use_all_secondary: seededLotDetailForFormFields?.useAllSecondary ?? false,
    is_confirmed: seededLotDetailForFormFields?.isConfirmed ?? false,
  })

  const catalogLotMarkedKit = Boolean(lot?.isKit)

  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const [deleteAttachmentIds, setDeleteAttachmentIds] = useState<number[]>([])
  const [formErrors, setFormErrors] = useState<Partial<Record<string, string>>>({})
  // Error summary banner lives at the top of the form; when validation fails the user may be
  // scrolled far down (long form / modal), so focus it back into view instead of silently
  // rejecting the submit.
  const errorBannerRef = useRef<HTMLDivElement>(null)
  const formTopRef = useRef<HTMLDivElement>(null)

  const applyFormErrors = (errors: Partial<Record<string, string>>) => {
    setFormErrors(errors)
    if (Object.keys(errors).length > 0) {
      requestAnimationFrame(() => {
        const target = errorBannerRef.current ?? formTopRef.current
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    }
  }

  // Ensures the top summary banner always renders when any field-level error exists, so the
  // user gets both an inline message next to the offending field AND a single "couldn't save"
  // banner at the top (which is also the scroll anchor).
  const withSummaryError = (
    errors: Partial<Record<string, string>>,
  ): Partial<Record<string, string>> => {
    if (errors.base || Object.keys(errors).length === 0) return errors
    return { ...errors, base: t('instruments.lotsForm.errors.saveFailed') }
  }

  // router.post is called directly below (not form.post/form.patch), so form.processing never
  // reflects the in-flight request. Track submission state ourselves to prevent double-submits.
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Legacy parity: when a supply type requires validation and an In Use date is entered while the
  // supply is not yet confirmed, saving is gated behind a confirmation popup. Confirming flips
  // is_confirmed → true (displayed as "Validated"); cancelling aborts the save entirely.
  const [showValidationConfirm, setShowValidationConfirm] = useState(false)
  const showSecondaryLotsSection = catalogLotMarkedKit && (isEdit ? lotDetailId != null : true)

  const [draftSecondaryLines, setDraftSecondaryLines] = useState<DraftSecondaryLineForCreate[]>(
    () => {
      if (isAddingDetail && catalogSecondaryLotsForLotRow.length > 0) {
        return catalogSecondaryLotsForLotRow.map((catalogEntry) => ({
          clientId: crypto.randomUUID(),
          secondaryLotId: catalogEntry.id,
          secondaryLotName: catalogEntry.name ?? '',
          secondaryLotDescription: catalogEntry.description ?? '',
          lotNumber: '',
          dateReceived: '',
          expirationDate: '',
          dateDiscarded: '',
        }))
      }
      if (isEdit && catalogSecondaryLotsForLotRow.length > 0) {
        const existingDetails = secondaryLotDetails ?? []
        return catalogSecondaryLotsForLotRow.map((catalogEntry) => {
          const existingDetail = existingDetails.find((d) => d.secondaryLotId === catalogEntry.id)
          return {
            clientId: crypto.randomUUID(),
            secondaryLotId: catalogEntry.id,
            secondaryLotName: catalogEntry.name ?? '',
            secondaryLotDescription: catalogEntry.description ?? '',
            lotNumber: existingDetail?.lotNumber ?? '',
            dateReceived: existingDetail?.dateReceived?.split('T')[0] ?? '',
            expirationDate: existingDetail?.expirationDate?.split('T')[0] ?? '',
            dateDiscarded: existingDetail?.dateDiscarded?.split('T')[0] ?? '',
          }
        })
      }
      return []
    },
  )

  const [dataFieldDrafts, setDataFieldDrafts] = useState<Record<number, DataFieldValueDraft[]>>(
    () => {
      if (!isAddingDetail) return {}
      return (lotTaskDataFields ?? []).reduce<Record<number, DataFieldValueDraft[]>>(
        (accumulator, field) => {
          accumulator[field.dataFieldId] = [
            {
              tempId: crypto.randomUUID(),
              existingId: null,
              validationTypeId: '',
              value: '',
              value2: '',
              isAcceptable: false,
              operatorType: '',
            },
          ]
          return accumulator
        },
        {},
      )
    },
  )

  const updateDataFieldDraftEntry = (
    dataFieldId: number,
    tempId: string,
    partial: Partial<DataFieldValueDraft>,
  ) => {
    setDataFieldDrafts((previous) => ({
      ...previous,
      [dataFieldId]: (previous[dataFieldId] ?? []).map((entry) =>
        entry.tempId === tempId ? { ...entry, ...partial } : entry,
      ),
    }))
  }

  const addDataFieldDraftEntry = (dataFieldId: number) => {
    setDataFieldDrafts((previous) => ({
      ...previous,
      [dataFieldId]: [
        ...(previous[dataFieldId] ?? []),
        {
          tempId: crypto.randomUUID(),
          existingId: null,
          validationTypeId: '',
          value: '',
          value2: '',
          isAcceptable: false,
          operatorType: '',
        },
      ],
    }))
  }

  const removeDataFieldDraftEntry = (dataFieldId: number, tempId: string) => {
    setDataFieldDrafts((previous) => {
      const updatedEntries = (previous[dataFieldId] ?? []).filter(
        (entry) => entry.tempId !== tempId,
      )
      return {
        ...previous,
        [dataFieldId]:
          updatedEntries.length > 0
            ? updatedEntries
            : [
                {
                  tempId: crypto.randomUUID(),
                  existingId: null,
                  validationTypeId: '',
                  value: '',
                  value2: '',
                  isAcceptable: false,
                  operatorType: '',
                },
              ],
      }
    })
  }

  const [definedValueDrafts, setDefinedValueDrafts] = useState<
    Record<number, DefinedValueAcceptabilityDraft>
  >(() =>
    (lotTaskDataFields ?? []).reduce<Record<number, DefinedValueAcceptabilityDraft>>(
      (accumulator, field) => {
        if (field.dataFieldType !== 'defined') return accumulator
        accumulator[field.dataFieldId] = (
          field.definedValues ?? []
        ).reduce<DefinedValueAcceptabilityDraft>((acc, dv) => {
          acc[dv.id] = dv.isAcceptable
          return acc
        }, {})
        return accumulator
      },
      {},
    ),
  )

  const toggleDefinedValueAcceptable = (
    dataFieldId: number,
    definedValueId: number,
    checked: boolean,
  ) => {
    setDefinedValueDrafts((previous) => ({
      ...previous,
      [dataFieldId]: { ...(previous[dataFieldId] ?? {}), [definedValueId]: checked },
    }))
  }

  const RANGE_BETWEEN_VALIDATION_TYPE = 'range_bt'
  const DEFINED_VALIDATION_TYPE = 'defined'
  const LINEAR_ALLOWED_VALIDATION_TYPES = new Set([
    'range_gt',
    'range_gt_eq',
    'range_lt',
    'range_lt_eq',
  ])

  const getAvailableValidationTypes = (field: NonNullable<typeof lotTaskDataFields>[number]) => {
    const types = field.validationTypes ?? []
    if (field.dataFieldType === 'linear') {
      return types.filter((vt) => LINEAR_ALLOWED_VALIDATION_TYPES.has(vt.validationType))
    }
    return types.filter((vt) => vt.validationType !== DEFINED_VALIDATION_TYPE)
  }

  // REBUILD-431 (JW 417) / REBUILD-614: single source of truth for whether the "Add More
  // Validation" control may render for a linked-supply data field. Centralising the rule keeps
  // the per-type behaviour explicit and prevents a future edit to the Number gate from
  // accidentally re-exposing it for Linear.
  //   - Linear  → NEVER. On a linked supply, Linear is a single validation-rule path, matching
  //               legacy linked-lot behaviour. No add-more control, regardless of the rule type
  //               the user picks (the control must stay hidden even if a Between-style type is
  //               somehow present in the options).
  //   - Defined → NEVER. Defined uses its own checkbox section, not the add-row surface.
  //   - Number  → NEVER. A linked supply starts with one required draft rule, and the shared
  //               single-rule predicate enforces the REBUILD-614 constraint.
  const canShowAddMoreValidation = (
    field: NonNullable<typeof lotTaskDataFields>[number],
    draftsForField: DataFieldValueDraft[],
  ): boolean => {
    if (field.dataFieldType === 'linear') return false
    if (field.dataFieldType === DEFINED_VALIDATION_TYPE) return false
    if (field.dataFieldType !== 'number') return false

    return canAddNumberAcceptableRule(draftsForField.length)
  }

  const mergeDraftSecondaryLinePartial = (
    draftLineClientIdentifier: string,
    partialDraftFields: Partial<DraftSecondaryLineForCreate>,
  ) => {
    setDraftSecondaryLines((previousDraftSecondaryLines) =>
      previousDraftSecondaryLines.map((draftLine) =>
        draftLine.clientId === draftLineClientIdentifier
          ? { ...draftLine, ...partialDraftFields }
          : draftLine,
      ),
    )
  }

  const removeDraftSecondaryLineByClientId = (draftLineClientIdentifier: string) => {
    setDraftSecondaryLines((previousDraftSecondaryLines) =>
      previousDraftSecondaryLines.filter(
        (draftLine) => draftLine.clientId !== draftLineClientIdentifier,
      ),
    )
  }

  const createDraftSecondaryLineWithParentLotDetailLotNumberBaselineBundle =
    (): DraftSecondaryLineForCreate => ({
      clientId: crypto.randomUUID(),
      secondaryLotName: '',
      secondaryLotDescription: '',
      lotNumber: form.data.lot_number.trim(),
      dateReceived: '',
      expirationDate: '',
      dateDiscarded: '',
    })

  const buildSubmitUrl = (basePath: string) => (fromModal ? `${basePath}?from_modal=1` : basePath)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (isSubmitting) return

    const data = form.data

    const clientErrors: Partial<Record<string, string>> = {}
    if (!data.lot_number.trim()) {
      clientErrors.lot_number = t('instruments.lotsForm.errors.lotNumberRequired')
    }
    const todayForLotValidation = todayDateStringForLotValidation()
    if (data.date_received && data.date_received > todayForLotValidation) {
      clientErrors.date_received = t('instruments.lotsForm.errors.dateReceivedInFuture')
    }
    if (data.date_in_use && data.date_in_use > todayForLotValidation) {
      clientErrors.date_in_use = t('instruments.lotsForm.errors.dateInUseInFuture')
    }
    if (data.date_discarded && data.date_discarded > todayForLotValidation) {
      clientErrors.date_discarded = t('instruments.lotsForm.errors.dateDiscardedInFuture')
    }
    if (data.date_received) {
      if (data.date_opened && data.date_opened < data.date_received) {
        clientErrors.date_opened = t('instruments.lotsForm.errors.openBeforeReceived')
      }
      if (!clientErrors.date_in_use && data.date_in_use && data.date_in_use < data.date_received) {
        clientErrors.date_in_use = t('instruments.lotsForm.errors.inUseBeforeReceived')
      }
      if (
        !clientErrors.date_discarded &&
        data.date_discarded &&
        data.date_discarded < data.date_received
      ) {
        clientErrors.date_discarded = t('instruments.lotsForm.errors.discardedBeforeReceived')
      }
    }
    // REBUILD-450: mirror LotDetail#expiration_date_must_not_be_in_past, which only runs when the
    // date changes — an expired/discarded lot must stay editable (comments, other dates) without
    // forcing a new expiration. Changing the expiration to another past date is still rejected.
    const persistedExpirationDate = isEdit ? lotDetail?.expirationDate?.split('T')[0] : undefined
    const expirationUnchangedOnEdit =
      persistedExpirationDate != null && data.expiration_date === persistedExpirationDate
    if (
      data.expiration_date &&
      data.expiration_date < todayForLotValidation &&
      !expirationUnchangedOnEdit
    ) {
      clientErrors.expiration_date = t('instruments.lotsForm.errors.expirationInPast')
    }
    if (
      !clientErrors.expiration_date &&
      data.expiration_date &&
      data.date_received &&
      data.expiration_date < data.date_received
    ) {
      clientErrors.expiration_date = t('instruments.lotsForm.errors.expirationBeforeReceived')
    }
    if (
      data.opened_expiration &&
      data.expiration_date &&
      data.opened_expiration > data.expiration_date
    ) {
      clientErrors.opened_expiration = t(
        'instruments.lotsForm.errors.openedExpirationExceedsExpiration',
      )
    }
    if (Object.keys(clientErrors).length > 0) {
      applyFormErrors(withSummaryError(clientErrors))
      return
    }

    for (const secondaryLine of draftSecondaryLines) {
      if (secondaryLine.dateReceived) {
        if (
          secondaryLine.expirationDate &&
          secondaryLine.expirationDate < secondaryLine.dateReceived
        ) {
          clientErrors.base = t('instruments.lotsForm.errors.expirationBeforeReceived')
        }
        if (
          secondaryLine.dateDiscarded &&
          secondaryLine.dateDiscarded < secondaryLine.dateReceived
        ) {
          clientErrors.base = t('instruments.lotsForm.errors.discardedBeforeReceived')
        }
      }
      if (secondaryLine.dateReceived && secondaryLine.dateReceived > todayForLotValidation) {
        clientErrors.base = t('instruments.lotsForm.errors.dateReceivedInFuture')
      }
      if (secondaryLine.dateDiscarded && secondaryLine.dateDiscarded > todayForLotValidation) {
        clientErrors.base = t('instruments.lotsForm.errors.dateDiscardedInFuture')
      }
    }
    if (Object.keys(clientErrors).length > 0) {
      applyFormErrors(clientErrors)
      return
    }

    if (isAddingDetail && lotTaskDataFields) {
      for (const field of lotTaskDataFields) {
        if (field.dataFieldType === DEFINED_VALIDATION_TYPE) {
          const acceptabilityMap = definedValueDrafts[field.dataFieldId] ?? {}
          const hasAcceptable = Object.values(acceptabilityMap).some(Boolean)
          if ((field.definedValues ?? []).length > 0 && !hasAcceptable) {
            clientErrors[`data_field_${field.dataFieldId}`] = t(
              'instruments.lotsForm.errors.definedValueAcceptableRequired',
            )
          }
        } else {
          const drafts = dataFieldDrafts[field.dataFieldId] ?? []
          const hasAnyFilled = drafts.some((entry) => {
            if (!entry.validationTypeId || !entry.value.trim()) return false
            // Between with only min is still a filled entry — hasBetweenInvalid fires the
            // specific "fill valid between values" error; we must not pre-empt it here with the
            // generic "required" message.
            return true
          })
          if (!hasAnyFilled) {
            clientErrors[`data_field_${field.dataFieldId}`] = t(
              'instruments.lotsForm.errors.dataFieldValueRequired',
            )
          }

          const hasBetweenInvalid = drafts.some((entry) => {
            if (!entry.validationTypeId || !entry.value.trim()) return false
            const selectedType = (field.validationTypes ?? []).find(
              (vt) => vt.id === Number(entry.validationTypeId),
            )
            if (selectedType?.validationType !== RANGE_BETWEEN_VALIDATION_TYPE) return false
            // Between requires both bounds — a missing max is also invalid.
            if (!entry.value2.trim()) return true
            const min = parseFloat(entry.value)
            const max = parseFloat(entry.value2)
            if (Number.isNaN(min) || Number.isNaN(max)) return true
            return min > max
          })
          if (hasBetweenInvalid && !clientErrors[`data_field_${field.dataFieldId}`]) {
            clientErrors[`data_field_${field.dataFieldId}`] = t(
              'instruments.lotsForm.errors.betweenValuesInvalid',
            )
          }
        }
      }
    }

    if (Object.keys(clientErrors).length > 0) {
      applyFormErrors(clientErrors)
      return
    }
    setFormErrors({})

    // Legacy parity: gate the save behind the validation confirmation popup when the supply type
    // requires validation, an In Use date is set, and it has not yet been confirmed. Cancelling
    // the popup aborts the save; confirming proceeds with is_confirmed flipped to true.
    if (data.lot_requires_validation && data.date_in_use && !data.is_confirmed) {
      setShowValidationConfirm(true)
      return
    }

    performSubmit()
  }

  const performSubmit = (confirmedOverride?: boolean) => {
    if (isSubmitting) return

    const data = form.data
    const isConfirmedForSubmit = confirmedOverride ?? data.is_confirmed

    const filteredDraftSecondaryLotLines = catalogLotMarkedKit
      ? draftSecondaryLines.filter(
          (line) => line.secondaryLotName.trim().length > 0 || line.secondaryLotId != null,
        )
      : []

    const formPayload = new FormData()

    if (lotIdForDetail != null) formPayload.append('lot_id', String(lotIdForDetail))

    formPayload.append('lot_detail[lot_number]', data.lot_number)
    formPayload.append('lot_detail[manufacturer]', data.manufacturer)
    formPayload.append('lot_detail[product]', data.product)
    formPayload.append('lot_detail[description]', data.description)
    formPayload.append('lot_detail[site_id]', '')
    formPayload.append('lot_detail[date_received]', data.date_received || '')
    formPayload.append('lot_detail[date_opened]', data.date_opened || '')
    formPayload.append('lot_detail[date_in_use]', data.date_in_use || '')
    formPayload.append('lot_detail[date_discarded]', data.date_discarded || '')
    formPayload.append('lot_detail[expiration_date]', data.expiration_date || '')
    formPayload.append('lot_detail[opened_expiration]', data.opened_expiration || '')
    formPayload.append('lot_detail[other_identifier]', data.other_identifier)
    formPayload.append('lot_detail[comments]', data.comments)
    formPayload.append('lot_detail[use_all_secondary]', data.use_all_secondary ? '1' : '0')
    formPayload.append('lot_detail[is_confirmed]', isConfirmedForSubmit ? '1' : '0')

    filteredDraftSecondaryLotLines.forEach((lineItem, index) => {
      const prefix = `secondary_lot_lines[${index}]`
      if (lineItem.secondaryLotId) {
        formPayload.append(`${prefix}[secondary_lot_id]`, String(lineItem.secondaryLotId))
      }
      formPayload.append(`${prefix}[secondary_lot_name]`, lineItem.secondaryLotName.trim())
      formPayload.append(`${prefix}[secondary_lot_detail][lot_number]`, lineItem.lotNumber || '')
      formPayload.append(
        `${prefix}[secondary_lot_detail][date_received]`,
        lineItem.dateReceived || '',
      )
      formPayload.append(
        `${prefix}[secondary_lot_detail][expiration_date]`,
        lineItem.expirationDate || '',
      )
      formPayload.append(
        `${prefix}[secondary_lot_detail][date_discarded]`,
        lineItem.dateDiscarded || '',
      )
    })

    pendingFiles.forEach((pf) => {
      formPayload.append('attachments[]', pf.file)
      formPayload.append('attachment_descriptions[]', pf.description)
    })

    deleteAttachmentIds.forEach((id) => {
      formPayload.append('delete_attachment_ids[]', String(id))
    })

    if (isAddingDetail) {
      Object.entries(dataFieldDrafts).forEach(([dataFieldId, entries]) => {
        entries.forEach((entry, entryIndex) => {
          if (!entry.validationTypeId && !entry.value) return
          const prefix = `data_field_values[${dataFieldId}][${entryIndex}]`
          formPayload.append(`${prefix}[validation_type_id]`, entry.validationTypeId)
          const isBetween =
            (lotTaskDataFields ?? [])
              .find((f) => f.dataFieldId === Number(dataFieldId))
              ?.validationTypes.find((vt) => vt.id === Number(entry.validationTypeId))
              ?.validationType === RANGE_BETWEEN_VALIDATION_TYPE
          formPayload.append(
            `${prefix}[value]`,
            isBetween && entry.value2
              ? formatBetweenBounds(entry.value, entry.value2)
              : entry.value,
          )
          formPayload.append(`${prefix}[is_acceptable]`, '1')
          if (entryIndex === 0 && entry.operatorType) {
            formPayload.append(`${prefix}[operator_type]`, entry.operatorType)
          }
        })
      })

      Object.entries(definedValueDrafts).forEach(([dataFieldId, acceptabilityMap]) => {
        let index = 0
        Object.entries(acceptabilityMap).forEach(([definedValueId, isAcceptable]) => {
          const prefix = `defined_field_values[${dataFieldId}][${index}]`
          formPayload.append(`${prefix}[defined_value_id]`, definedValueId)
          formPayload.append(`${prefix}[is_acceptable]`, isAcceptable ? '1' : '0')
          index++
        })
      })
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const submitOptions: any = {
      preserveScroll: true,
      onError: (errors: Record<string, string>) => {
        applyFormErrors(errors)
      },
      onFinish: () => setIsSubmitting(false),
      ...(fromModal
        ? {
            preserveState: true,
            ...(reloadOnly ? { only: reloadOnly } : {}),
            onSuccess: (page: { props: { flash?: { lot_detail_id?: number } } }) => {
              onSuccess?.(page.props.flash?.lot_detail_id)
            },
          }
        : {}),
    }

    const url = buildSubmitUrl(
      isEdit && lotDetailId
        ? instrumentLotPath(instrumentId, lotDetailId)
        : instrumentLotsPath(instrumentId),
    )

    setIsSubmitting(true)

    if (isEdit && lotDetailId) {
      formPayload.append('_method', 'PATCH')
      router.post(url, formPayload, submitOptions)
    } else {
      router.post(url, formPayload, submitOptions)
    }
  }

  const handleValidationConfirm = () => {
    setShowValidationConfirm(false)
    form.setData('is_confirmed', true)
    performSubmit(true)
  }

  const handleValidationCancel = () => {
    setShowValidationConfirm(false)
    setIsSubmitting(false)
  }

  const serverErrors = formErrors

  return (
    <div ref={formTopRef} className="space-y-6">
      <LotNotesBanner notes={lot?.notes} />
      {serverErrors.base && (
        <div
          ref={errorBannerRef}
          className="rounded border border-destructive bg-destructive/10 px-4 py-3"
        >
          <p className="text-sm text-destructive">{serverErrors.base}</p>
        </div>
      )}

      <form
        id={INSTRUMENT_LOT_MAIN_FORM_DOM_IDENTIFIER}
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        {/* REBUILD-498: the "User" role lacks lot create/update, so the reagent/supply edit
          fields render disabled (read-only) rather than editable-but-unsavable. The Save button
          is already hidden and a NoAccessNotice shown below when !canSubmitLot. The fieldset keeps
          native inputs non-interactive; the form submit is additionally guarded server-side via
          Pundit. */}
        <fieldset disabled={!canSubmitLot} className="space-y-6 disabled:opacity-100">
          <section className="rounded-lg border p-4 space-y-4">
            <h2 className="text-lg font-semibold">
              {t('instruments.lotsForm.sections.lotDetail')}
            </h2>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="lot_number">{t('instruments.lotsForm.fields.lotNumber')}</Label>
                <Input
                  id="lot_number"
                  value={form.data.lot_number}
                  onChange={(event) => form.setData('lot_number', event.target.value)}
                  placeholder={t('instruments.lotsForm.placeholders.enterLotDetailNumber')}
                />
                <FieldError message={serverErrors.lot_number} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="date_received">
                  {t('instruments.lotsForm.fields.dateReceived')}
                </Label>
                <DatePickerInput
                  id="date_received"
                  value={form.data.date_received}
                  onChange={(nextDateValue) => form.setData('date_received', nextDateValue)}
                  placeholder={optionalLotFormDateFieldPlaceholder}
                />
                <FieldError message={serverErrors.date_received} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="expiration_date">
                  {t('instruments.lotsForm.fields.expirationDate')}
                </Label>
                <DatePickerInput
                  id="expiration_date"
                  value={form.data.expiration_date}
                  onChange={(nextDateValue) => form.setData('expiration_date', nextDateValue)}
                  placeholder={optionalLotFormDateFieldPlaceholder}
                />
                <FieldError message={serverErrors.expiration_date} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="date_opened">{t('instruments.lotsForm.fields.dateOpened')}</Label>
                <DatePickerInput
                  id="date_opened"
                  value={form.data.date_opened}
                  onChange={(nextDateValue) => form.setData('date_opened', nextDateValue)}
                  placeholder={optionalLotFormDateFieldPlaceholder}
                />
                <FieldError message={serverErrors.date_opened} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="opened_expiration">
                  {t('instruments.lotsForm.fields.openedExpiration')}
                </Label>
                <DatePickerInput
                  id="opened_expiration"
                  value={form.data.opened_expiration}
                  onChange={(nextDateValue) => form.setData('opened_expiration', nextDateValue)}
                  placeholder={optionalLotFormDateFieldPlaceholder}
                />
                <FieldError message={serverErrors.opened_expiration} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="date_in_use">{t('instruments.lotsForm.fields.dateInUse')}</Label>
                <DatePickerInput
                  id="date_in_use"
                  value={form.data.date_in_use}
                  onChange={(nextDateValue) => form.setData('date_in_use', nextDateValue)}
                  placeholder={optionalLotFormDateFieldPlaceholder}
                />
                <FieldError message={serverErrors.date_in_use} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="other_identifier">
                  {t('instruments.lotsForm.fields.otherIdentifier')}
                </Label>
                <Input
                  id="other_identifier"
                  value={form.data.other_identifier}
                  onChange={(event) => form.setData('other_identifier', event.target.value)}
                  placeholder={t('instruments.lotsForm.placeholders.optional')}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="date_discarded">
                  {t('instruments.lotsForm.fields.dateDiscarded')}
                </Label>
                <DatePickerInput
                  id="date_discarded"
                  value={form.data.date_discarded}
                  onChange={(nextDateValue) => form.setData('date_discarded', nextDateValue)}
                  placeholder={optionalLotFormDateFieldPlaceholder}
                />
                <FieldError message={serverErrors.date_discarded} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="manufacturer">
                  {t('instruments.lotsForm.fields.manufacturer')}
                </Label>
                <Input
                  id="manufacturer"
                  value={form.data.manufacturer}
                  onChange={(event) => form.setData('manufacturer', event.target.value)}
                  placeholder={t('instruments.lotsForm.placeholders.optional')}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="product">{t('instruments.lotsForm.fields.product')}</Label>
                <Input
                  id="product"
                  value={form.data.product}
                  onChange={(event) => form.setData('product', event.target.value)}
                  placeholder={t('instruments.lotsForm.placeholders.optional')}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="description">{t('instruments.lotsForm.fields.description')}</Label>
              <Input
                id="description"
                value={form.data.description}
                onChange={(event) => form.setData('description', event.target.value)}
                placeholder={t('instruments.lotsForm.placeholders.optional')}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="comments">{t('instruments.lotsForm.fields.comments')}</Label>
              <Textarea
                id="comments"
                value={form.data.comments}
                onChange={(e) => form.setData('comments', e.target.value)}
                placeholder={t('instruments.lotsForm.placeholders.optional')}
                rows={3}
              />
            </div>

            {form.data.lot_requires_validation && (
              <div className="flex items-center gap-2">
                <Label>{t('instruments.lotsForm.fields.validationStatus')}</Label>
                <span
                  className={
                    form.data.is_confirmed
                      ? 'text-sm font-medium text-emerald-600'
                      : 'text-sm font-medium text-muted-foreground'
                  }
                >
                  {form.data.is_confirmed
                    ? t('instruments.lotsForm.fields.validated')
                    : t('instruments.lotsForm.fields.notValidated')}
                </span>
              </div>
            )}
          </section>

          {showSecondaryLotsSection ? (
            <section
              className="rounded-lg border p-4 space-y-4"
              aria-labelledby="lot-form-secondary-heading"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <h2 id="lot-form-secondary-heading" className="text-lg font-semibold">
                  {t('instruments.lotsForm.sections.secondaryLots')}
                  {draftSecondaryLines.length > 0 ? (
                    <span className="ml-2 text-sm font-normal text-muted-foreground">
                      ({draftSecondaryLines.length})
                    </span>
                  ) : null}
                </h2>
                {!isEdit && !isAddingDetail ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() =>
                      setDraftSecondaryLines((previousDraftLines) => [
                        ...previousDraftLines,
                        createDraftSecondaryLineWithParentLotDetailLotNumberBaselineBundle(),
                      ])
                    }
                  >
                    {t('instruments.lotsForm.buttons.addSecondary')}
                  </Button>
                ) : null}
              </div>

              {draftSecondaryLines.length === 0 && !isEdit ? (
                <p className="text-sm text-muted-foreground">
                  {t('instruments.lotsForm.noSecondaryLots')}
                </p>
              ) : null}

              <div className="space-y-4">
                {draftSecondaryLines.map((draftSecondaryLineRecord) => {
                  const isCatalogSeeded = draftSecondaryLineRecord.secondaryLotId != null
                  return (
                    <div
                      key={draftSecondaryLineRecord.clientId}
                      className="space-y-3 rounded-md border bg-muted/20 p-3"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {isCatalogSeeded
                              ? draftSecondaryLineRecord.secondaryLotName
                              : t('instruments.lotsForm.secondaryDraftRowLabel')}
                          </p>
                          {isCatalogSeeded && draftSecondaryLineRecord.secondaryLotDescription && (
                            <p className="text-xs text-muted-foreground">
                              {draftSecondaryLineRecord.secondaryLotDescription}
                            </p>
                          )}
                        </div>
                        {!isCatalogSeeded && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 shrink-0 text-destructive"
                            onClick={() =>
                              removeDraftSecondaryLineByClientId(draftSecondaryLineRecord.clientId)
                            }
                          >
                            {t('instruments.lotsForm.buttons.removeSecondaryLot')}
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {!isCatalogSeeded && (
                          <div className="space-y-1.5 sm:max-w-xs">
                            <Label htmlFor={`sec-name-${draftSecondaryLineRecord.clientId}`}>
                              {t('instruments.lotsShow.table.name')} *
                            </Label>
                            <Input
                              id={`sec-name-${draftSecondaryLineRecord.clientId}`}
                              value={draftSecondaryLineRecord.secondaryLotName}
                              onChange={(inputChangeEventFromUser) =>
                                mergeDraftSecondaryLinePartial(draftSecondaryLineRecord.clientId, {
                                  secondaryLotName: inputChangeEventFromUser.target.value,
                                })
                              }
                              placeholder={t('instruments.lotsForm.placeholders.secondaryLotName')}
                            />
                          </div>
                        )}
                        <div className="space-y-1.5">
                          <Label htmlFor={`sec-lotnum-${draftSecondaryLineRecord.clientId}`}>
                            {t('instruments.lotsForm.fields.lotNumber')}
                          </Label>
                          <Input
                            id={`sec-lotnum-${draftSecondaryLineRecord.clientId}`}
                            value={draftSecondaryLineRecord.lotNumber}
                            onChange={(inputChangeEventFromUser) =>
                              mergeDraftSecondaryLinePartial(draftSecondaryLineRecord.clientId, {
                                lotNumber: inputChangeEventFromUser.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`sec-received-${draftSecondaryLineRecord.clientId}`}>
                            {t('instruments.lotsForm.fields.dateReceived')}
                          </Label>
                          <DatePickerInput
                            id={`sec-received-${draftSecondaryLineRecord.clientId}`}
                            value={draftSecondaryLineRecord.dateReceived}
                            placeholder={optionalLotFormDateFieldPlaceholder}
                            onChange={(nextDateValueFromUser) =>
                              mergeDraftSecondaryLinePartial(draftSecondaryLineRecord.clientId, {
                                dateReceived: nextDateValueFromUser,
                              })
                            }
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`sec-exp-${draftSecondaryLineRecord.clientId}`}>
                            {t('instruments.lotsForm.fields.expirationDate')}
                          </Label>
                          <DatePickerInput
                            id={`sec-exp-${draftSecondaryLineRecord.clientId}`}
                            value={draftSecondaryLineRecord.expirationDate}
                            placeholder={optionalLotFormDateFieldPlaceholder}
                            onChange={(nextDateValueFromUser) =>
                              mergeDraftSecondaryLinePartial(draftSecondaryLineRecord.clientId, {
                                expirationDate: nextDateValueFromUser,
                              })
                            }
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`sec-discarded-${draftSecondaryLineRecord.clientId}`}>
                            {t('instruments.lotsForm.fields.dateDiscarded')}
                          </Label>
                          <DatePickerInput
                            id={`sec-discarded-${draftSecondaryLineRecord.clientId}`}
                            value={draftSecondaryLineRecord.dateDiscarded}
                            placeholder={optionalLotFormDateFieldPlaceholder}
                            onChange={(nextDateValueFromUser) =>
                              mergeDraftSecondaryLinePartial(draftSecondaryLineRecord.clientId, {
                                dateDiscarded: nextDateValueFromUser,
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          ) : null}
        </fieldset>
      </form>

      {isEdit && (lotTaskDataFields ?? []).length > 0 && (
        <div className="rounded-md border overflow-hidden">
          {(lotTaskDataFields ?? []).map((field) => (
            <div key={field.dataFieldId} className="border-b last:border-b-0">
              <div className="flex items-center gap-1.5 bg-muted px-3 py-2 text-sm font-semibold">
                <span>{lot?.name}</span>
                <Link2 className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="font-normal text-muted-foreground">
                  {t('instruments.lotsForm.linkedToTask')}
                </span>
                <span>{field.taskName}</span>
              </div>
              <div className="px-3 py-2 space-y-1">
                <p className="text-xs text-muted-foreground">
                  {t('instruments.lotsForm.dataFieldValuesTitle')}
                </p>
                {field.dataFieldType === DEFINED_VALIDATION_TYPE ? (
                  (field.definedValues ?? []).length > 0 ? (
                    (field.definedValues ?? []).map((dv) => (
                      <div key={dv.id} className="flex items-center gap-6 py-0.5 text-sm">
                        <span className="font-semibold">{dv.value}</span>
                        <span
                          className={dv.isAcceptable ? 'text-emerald-600' : 'text-muted-foreground'}
                        >
                          {dv.isAcceptable
                            ? t('instruments.lotsForm.acceptableLabel')
                            : t('instruments.lotsForm.notAcceptableLabel')}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center gap-6 py-0.5 text-sm">
                      <span className="font-semibold">{field.dataFieldLabel}</span>
                      <span className="text-muted-foreground italic">
                        {t('instruments.lotsForm.noDataFieldValuesConfigured')}
                      </span>
                    </div>
                  )
                ) : (field.existingValues ?? []).length > 0 ? (
                  (field.existingValues ?? []).map((val) => (
                    <div key={val.id} className="flex items-center gap-6 py-0.5 text-sm">
                      <span className="font-semibold">{field.dataFieldLabel}</span>
                      <span className="text-muted-foreground">
                        {val.validationTypeDesc}
                        {val.value && (
                          <span className="ml-1">
                            {(() => {
                              const rangeBounds =
                                field.dataFieldType === 'number'
                                  ? parseNumericRangeBounds(val.value)
                                  : null
                              return rangeBounds
                                ? `${rangeBounds[0]} ${t('instruments.lotsForm.betweenValueSeparator')} ${rangeBounds[1]}`
                                : val.value
                            })()}
                          </span>
                        )}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center gap-6 py-0.5 text-sm">
                    <span className="font-semibold">{field.dataFieldLabel}</span>
                    <span className="text-muted-foreground italic">
                      {t('instruments.lotsForm.noDataFieldValuesConfigured')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {isAddingDetail && (lotTaskDataFields ?? []).length > 0 && (
        <div className="rounded-md border overflow-hidden">
          {(lotTaskDataFields ?? []).map((field) => {
            const draftsForField = dataFieldDrafts[field.dataFieldId] ?? []
            const isDefinedField = field.dataFieldType === DEFINED_VALIDATION_TYPE

            return (
              <div key={field.dataFieldId} className="border-b last:border-b-0">
                <div className="flex items-center gap-1.5 bg-muted px-3 py-2 text-sm font-semibold">
                  <span>{lot?.name}</span>
                  <Link2 className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="font-normal text-muted-foreground">
                    {t('instruments.lotsForm.linkedToTask')}
                  </span>
                  <span>{field.taskName}</span>
                </div>
                <div className="px-3 py-3 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    {t('instruments.lotsForm.dataFieldValuesWillNeedUpdated')}
                  </p>
                  <p className="text-sm font-semibold">{field.dataFieldLabel}</p>

                  {isDefinedField ? (
                    <div className="space-y-1.5">
                      {(field.definedValues ?? []).length === 0 ? (
                        <p className="text-sm text-muted-foreground italic">
                          {t('instruments.lotsForm.noDataFieldValuesConfigured')}
                        </p>
                      ) : (
                        (field.definedValues ?? []).map((dv) => {
                          const isAcceptableDraft =
                            definedValueDrafts[field.dataFieldId]?.[dv.id] ?? dv.isAcceptable
                          return (
                            <div key={dv.id} className="flex items-center gap-2">
                              <Checkbox
                                id={`dv-${field.dataFieldId}-${dv.id}`}
                                checked={isAcceptableDraft}
                                onCheckedChange={(checked) =>
                                  toggleDefinedValueAcceptable(
                                    field.dataFieldId,
                                    dv.id,
                                    Boolean(checked),
                                  )
                                }
                              />
                              <Label
                                htmlFor={`dv-${field.dataFieldId}-${dv.id}`}
                                className="text-sm"
                              >
                                {dv.value}
                              </Label>
                              {/* Legacy parity (REBUILD-405 / JW 412): the acceptable state must
                                  reflect the checkbox, not a static label. A defined option is
                                  "acceptable" ONLY after the user selects it — until then it shows
                                  "Not acceptable", matching the edit-mode indicator. */}
                              <span
                                className={
                                  isAcceptableDraft
                                    ? 'text-xs text-emerald-600'
                                    : 'text-xs text-muted-foreground'
                                }
                              >
                                {isAcceptableDraft
                                  ? t('instruments.lotsForm.acceptableLabel')
                                  : t('instruments.lotsForm.notAcceptableLabel')}
                              </span>
                            </div>
                          )
                        })
                      )}
                    </div>
                  ) : (
                    <>
                      {draftsForField.map((entry, entryIndex) => (
                        <div key={entry.tempId} className="space-y-1.5">
                          {entryIndex === 1 &&
                            (field.dataFieldType === 'number' ||
                              field.dataFieldType === 'linear') && (
                              <div className="flex items-center gap-2 py-0.5">
                                <Select
                                  value={draftsForField[0].operatorType || 'OR'}
                                  onValueChange={(selectedValue) =>
                                    updateDataFieldDraftEntry(
                                      field.dataFieldId,
                                      draftsForField[0].tempId,
                                      {
                                        operatorType: selectedValue as 'OR' | 'AND',
                                      },
                                    )
                                  }
                                >
                                  <SelectTrigger className="w-20 h-7 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="OR">
                                      {t('instruments.lotsForm.operatorOr')}
                                    </SelectItem>
                                    <SelectItem value="AND">
                                      {t('instruments.lotsForm.operatorAnd')}
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          <div className="flex items-center gap-2 flex-wrap">
                            <Label className="text-sm text-muted-foreground shrink-0">
                              {t('instruments.lotsForm.acceptableIf')}
                            </Label>
                            <Select
                              value={entry.validationTypeId}
                              onValueChange={(selectedValue) =>
                                updateDataFieldDraftEntry(field.dataFieldId, entry.tempId, {
                                  validationTypeId: selectedValue,
                                })
                              }
                            >
                              <SelectTrigger className="w-44">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {getAvailableValidationTypes(field).map((validationType) => (
                                  <SelectItem
                                    key={validationType.id}
                                    value={String(validationType.id)}
                                  >
                                    {validationType.validationTypeDesc}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              className="w-24"
                              aria-label={t('instruments.lotsForm.acceptableBetweenMin')}
                              value={entry.value}
                              onChange={(changeEvent) => {
                                const raw = changeEvent.target.value
                                if (raw !== '' && !/^-?\d*\.?\d*$/.test(raw)) return
                                updateDataFieldDraftEntry(field.dataFieldId, entry.tempId, {
                                  value: raw,
                                })
                              }}
                            />
                            {(field.validationTypes ?? []).find(
                              (vt) => vt.id === Number(entry.validationTypeId),
                            )?.validationType === RANGE_BETWEEN_VALIDATION_TYPE && (
                              <Input
                                className="w-24"
                                placeholder={t('instruments.lotsForm.betweenValueSeparator')}
                                value={entry.value2}
                                onChange={(changeEvent) => {
                                  const raw = changeEvent.target.value
                                  if (raw !== '' && !/^-?\d*\.?\d*$/.test(raw)) return
                                  updateDataFieldDraftEntry(field.dataFieldId, entry.tempId, {
                                    value2: raw,
                                  })
                                }}
                              />
                            )}
                            {draftsForField.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive px-2"
                                onClick={() =>
                                  removeDataFieldDraftEntry(field.dataFieldId, entry.tempId)
                                }
                              >
                                ×
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* REBUILD-431 (JW 417) / REBUILD-614: linked-supply Linear and Number
                          fields each accept one validation rule, so "Add More Validation" stays
                          hidden. Defined uses its own section. The per-type rule lives in
                          canShowAddMoreValidation. */}
                      {canShowAddMoreValidation(field, draftsForField) && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addDataFieldDraftEntry(field.dataFieldId)}
                        >
                          {t('instruments.lotsForm.addMoreValidation')}
                        </Button>
                      )}
                    </>
                  )}
                  <FieldError message={serverErrors[`data_field_${field.dataFieldId}`]} />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {lotDetailId != null || isAddingDetail ? (
        <LotFormAttachmentsSection
          attachmentsList={documentAttachments}
          pendingFiles={pendingFiles}
          onPendingFilesChange={setPendingFiles}
          onDeleteExisting={(id) => setDeleteAttachmentIds((prev) => [...prev, id])}
          deletedAttachmentIds={deleteAttachmentIds}
          permittedToCreateAttachmentDocuments={permittedToCreateLotAttachmentDocuments}
          permittedToDeleteAttachmentDocuments={permittedToDeleteLotAttachmentDocuments}
        />
      ) : null}
      {!canSubmitLot && <NoAccessNotice />}

      <div className="flex gap-3">
        {canSubmitLot && (
          <Button
            type="submit"
            form={INSTRUMENT_LOT_MAIN_FORM_DOM_IDENTIFIER}
            disabled={isSubmitting}
          >
            {t('instruments.lotsForm.buttons.save')}
          </Button>
        )}
        <Button type="button" variant="outline" onClick={() => onCancel?.()}>
          {t('instruments.lotsForm.buttons.cancel')}
        </Button>
      </div>

      <AlertDialog
        open={showValidationConfirm}
        onOpenChange={(open) => {
          if (!open) handleValidationCancel()
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('instruments.lotsForm.validationConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('instruments.lotsForm.validationConfirm.message')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleValidationCancel}>
              {t('instruments.lotsForm.validationConfirm.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleValidationConfirm}>
              {t('instruments.lotsForm.validationConfirm.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
